
import { Options, mixins } from 'vue-class-component'
import { maska } from 'maska'
import { ICampaign, IDateRangePicker, IPopup } from '@/utils/types'
import { Watch } from 'vue-property-decorator'
import cloneDeep from 'lodash/cloneDeep'
import { ACTION_CAMPAIGN, ACTION_POPUP } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'
import { constant } from '@/utils/constants'
import { Formater } from '@/utils/formater'
import { DatetimeUtils, lastNMonth, now } from '@/utils/datetime'
import BaseFormMixin from '../common/mixins/BaseFormMixin.vue'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'

@Options({
  components: { CLoading, DateRangeSelectorV2, TestModeSelector },
  directives: { maska },
  emits: [],
})
export default class PopupList extends mixins(BaseFormMixin) {
  loading = true
  firstLoad = true
  selectedCampaignId = ''
  isTestMode = false
  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  campaigns: ICampaign[] = []

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  get campaignId() {
    return (this.$route.params.campaign_id as string) || ''
  }

  get popups() {
    return cloneDeep(this.$store.getters.popups) || []
  }

  @Watch('campaignId', { immediate: true })
  async loadData() {
    if (!this.selectedAppId) {
      return
    }
    this.firstLoad = true

    this.dateRange = DatetimeUtils.getDateRange('last30Days')

    const cams = await this.$store.dispatch(ACTION_CAMPAIGN.LOAD_COMPACT_ITEMS, {
      app_id: this.selectedAppId,
      is_traffic_source: false,
    })

    this.campaigns = cams

    this.selectedCampaignId = this.campaignId
    await this.loadPopups()
    this.loading = false
    this.firstLoad = false
  }

  @Watch('isTestMode')
  async isTestModeChange() {
    this.loadPopups()
  }

  @Watch('selectedCampaignId')
  handleChangeCampaign() {
    if (this.firstLoad) {
      return
    }
    this.loadPopups()
  }

  changeDateRange(value) {
    if (this.firstLoad) {
      return
    }

    this.dateRange = value
    this.loadPopups()
  }

  async loadPopups() {
    await this.$store.dispatch(ACTION_POPUP.LOAD_ITEMS, {
      app_id: this.selectedAppId,
      is_test_mode: this.isTestMode,
      campaign_id: this.selectedCampaignId,
      date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
      date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
    })
  }

  async updateStatusMode(item: IPopup, statusMode) {
    let mod = ACTION_POPUP.IS_ACTIVE
    if (statusMode) {
      mod = ACTION_POPUP.TEST_MODE
    }
    const isSuccess = await this.$store.dispatch(mod, item)
    if (isSuccess) {
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }

    return isSuccess
  }

  async handleToggleActive(record: IPopup) {
    if (!record.is_active) {
      this.updateStatusMode(record, false)
    } else {
      this.$q
        .dialog({
          title: this.$t('messages.confirm'),
          message: this.$t('messages.are_you_sure_to_turn_on_active_for_this_item'),
          cancel: {
            flat: true,
            label: this.$t('messages.no'),
          },
          ok: {
            flat: true,
            label: this.$t('messages.yes'),
          },
          persistent: true,
        })
        .onOk(async () => {
          this.updateStatusMode(record, false)
        })
        .onCancel(async () => {
          record.is_active = false
        })
    }
  }

  async handleToggleTest(record: IPopup) {
    this.updateStatusMode(record, true)
  }

  onAdd() {
    this.goto('popup_setting', {
      app_id: this.selectedAppId,
      campaign_id: this.selectedCampaignId,
      action_type: constant.ACTION_TYPE.ADD,
    })
  }

  onEdit(data: IPopup) {
    return this.detailPath('popup_setting', {
      app_id: data.app_id,
      campaign_id: data.campaign_id,
      popup_id: data._id,
      action_type: constant.ACTION_TYPE.EDIT,
    })
  }

  onCopy(data: IPopup) {
    this.goto('popup_setting', {
      app_id: data.app_id,
      campaign_id: data.campaign_id,
      popup_id: data._id,
      action_type: constant.ACTION_TYPE.COPY,
    })
  }

  async onDelete(data: IPopup) {
    this.$q
      .dialog({
        title: this.$t('confirm'),
        message: this.$t('are_you_sure_you_want_to_delete'),
        cancel: true,
        persistent: true,
      })
      .onOk(async () => {
        const success = await this.$store.dispatch(ACTION_POPUP.DELETE, data._id)
        if (success) {
          this.$q.notify({
            message: this.$t('messages.deleted'),
            color: 'positive',
          })
        }
      })
  }

  onDetails(event, data: IPopup) {
    // Check if Ctrl (Windows) or Meta (Mac) is pressed
    if (event.ctrlKey || event.metaKey) {
      // Let the browser handle opening in a new tab
      return
    }
    // Otherwise, navigate programmatically
    event.preventDefault()

    this.goto('popup_details', {
      app_id: this.selectedAppId,
      campaign_id: data.campaign_id,
      popup_id: data._id,
      from_date: lastNMonth(1),
      to_date: now(),
    })
  }

  getDetailsUrl(data) {
    // Construct the URL as a string for direct navigation
    return this.$router.resolve({
      name: 'popup_details',
      params: {
        app_id: this.selectedAppId,
        campaign_id: data.campaign_id,
        popup_id: data._id,
        from_date: lastNMonth(1),
        to_date: now(),
      },
    }).href
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  detailPath(name: string, params = {}) {
    return {
      name: name,
      params: params,
    }
  }

  numberWithCommas(num) {
    return Formater.numberFormat(num)
  }

  roundNumber(num) {
    return Formater.round(num)
  }
}
